import { useState, type ReactNode } from 'react';

import { AppIcons, type AppIconName, Card } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

type BucketSectionTone = 'gray' | 'amber' | 'red' | 'green' | 'ai' | 'brand';
type CardTone = 'neutral' | 'amber' | 'red' | 'ai' | 'green' | 'brand';

const TONE_TO_CARD_TONE: Record<BucketSectionTone, CardTone> = {
  gray: 'neutral',
  brand: 'brand',
  amber: 'amber',
  green: 'green',
  red: 'red',
  ai: 'ai',
};

const HEAD_BG: Record<BucketSectionTone, string> = {
  gray: 'bg-muted',
  brand: 'bg-primary-tint/55',
  amber: 'bg-tone-amber-tint/55',
  green: 'bg-tone-green-tint/55',
  red: 'bg-tone-red-tint/55',
  ai: 'bg-ai-tint/55',
};

const HEAD_DIVIDER: Record<BucketSectionTone, string> = {
  gray: 'border-border-soft',
  brand: 'border-primary/22',
  amber: 'border-tone-amber-fg/22',
  green: 'border-tone-green-fg/22',
  red: 'border-tone-red-fg/22',
  ai: 'border-ai/22',
};

const GLYPH_TEXT: Record<BucketSectionTone, string> = {
  gray: 'text-muted-foreground',
  brand: 'text-primary',
  amber: 'text-tone-amber-fg',
  green: 'text-tone-green-fg',
  red: 'text-tone-red-fg',
  ai: 'text-ai',
};

const GLYPH_BORDER: Record<BucketSectionTone, string> = {
  gray: 'border-border-soft',
  brand: 'border-primary/25',
  amber: 'border-tone-amber-fg/25',
  green: 'border-tone-green-fg/25',
  red: 'border-tone-red-fg/25',
  ai: 'border-ai/25',
};

const COUNT_TEXT: Record<BucketSectionTone, string> = {
  gray: 'text-foreground',
  brand: 'text-primary',
  amber: 'text-tone-amber-fg',
  green: 'text-tone-green-fg',
  red: 'text-tone-red-fg',
  ai: 'text-ai',
};

interface BucketSectionProps {
  /** Section heading — appears as the head label. */
  title: string;
  /** One-line note rendered under the title. */
  note: string;
  /** Glyph icon (rendered inside a tone-tinted outlined square). */
  icon: AppIconName;
  tone?: BucketSectionTone;
  /**
   * Trailing serif counter — pass the row count of the embedded body,
   * or `null` while the count is still loading (renders a pulsing
   * placeholder and keeps a collapsible head interactive).
   */
  count: number | null;
  /**
   * When `true` the head becomes a button that toggles the body
   * visibility. Defaults to `false` — head stays static (like the
   * overview screen, where the body is always visible).
   */
  collapsible?: boolean;
  /**
   * Optional `data-testid` for assertions; both the wrapping Card
   * and the head pick it up so the test can target either.
   */
  testId?: string;
  /** Rendered inside the bucket body when expanded. */
  children: ReactNode;
}

/**
 * BucketSection — shared card-with-tone-head shell used by the
 * overview screen and the tasks board. Owns the visual rhythm
 * (Card outer + tone-tinted head + outlined glyph + serif counter)
 * so the two surfaces stay in lockstep when the design moves.
 */
export function BucketSection({
  title,
  note,
  icon,
  tone = 'gray',
  count,
  collapsible = false,
  testId,
  children,
}: Readonly<BucketSectionProps>): React.JSX.Element {
  const [open, setOpen] = useState(true);
  const isEmpty = count === 0;
  // Empty collapsible groups stay locked closed — the head is a
  // static row and the body never renders. Non-collapsible groups
  // always show their body so callers can render their own empty
  // placeholder.
  const interactive = collapsible && !isEmpty;
  const expanded = collapsible ? interactive && open : true;

  const head = (
    <>
      <span
        aria-hidden
        className={twcn(
          'inline-flex h-row-h w-row-h flex-shrink-0 items-center justify-center rounded-md border bg-surface',
          GLYPH_TEXT[tone],
          GLYPH_BORDER[tone],
        )}
      >
        <AppIcons name={icon} size={15} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-base font-medium tracking-display-soft text-foreground">{title}</span>
        <span className="text-micro text-muted-foreground">{note}</span>
      </div>
      <span
        className={twcn(
          'flex-shrink-0 font-serif text-xl font-medium leading-none tabular-nums tracking-display-condensed',
          COUNT_TEXT[tone],
        )}
      >
        {count === null ? (
          <span
            aria-hidden
            data-testid="bucket-count-loading"
            className="inline-block h-5 w-4 animate-pulse rounded-sm bg-border-soft"
          />
        ) : (
          count
        )}
      </span>
    </>
  );

  const headClassName = twcn(
    'flex items-center gap-3 border-b px-4 py-3 text-left',
    HEAD_BG[tone],
    HEAD_DIVIDER[tone],
  );

  return (
    <Card tone={TONE_TO_CARD_TONE[tone]}>
      <div data-testid={testId} className="flex flex-col overflow-hidden rounded-lg">
        {interactive ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => {
              setOpen((o) => !o);
            }}
            className={headClassName}
          >
            {head}
          </button>
        ) : (
          <header className={headClassName}>{head}</header>
        )}
        {expanded ? children : null}
      </div>
    </Card>
  );
}
